/**
 * Página — Obrigações Fiscais (DCTFWeb / GPS / DARF / GRRF)
 * GridProfile / NeuroSynx Ltda
 *
 * Esqueleto inicial: geração automática a partir de competência calculada,
 * registro manual de obrigações e marcação de pagamento.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Receipt, Wallet, Calculator, Calendar, Plus, Trash2, CheckCircle2,
  Clock, Loader2, AlertCircle, FileText, BadgeDollarSign,
} from 'lucide-react';
import useClientPagination from '../../hooks/useClientPagination';
import GridPagination from '../GridPagination';
import AskMayaButton from '../AskMayaButton';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtBRL = (v) => v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (s) => !s ? '—' : String(s).slice(0, 10).split('-').reverse().join('/');

const TIPOS = {
  DCTFWeb:     { nome: 'DCTFWeb',  cor: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  GPS:         { nome: 'GPS',      cor: 'bg-blue-50 text-blue-700 border-blue-300' },
  DARF_IRRF:   { nome: 'DARF IRRF',cor: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  DARF_PIS:    { nome: 'DARF PIS', cor: 'bg-teal-50 text-teal-700 border-teal-300' },
  DARF_COFINS: { nome: 'DARF COFINS', cor: 'bg-cyan-50 text-cyan-700 border-cyan-300' },
  GRRF:        { nome: 'GRRF',     cor: 'bg-amber-50 text-amber-700 border-amber-300' },
};

const STATUS_BADGE = {
  rascunho:  { cor: 'bg-slate-100 text-slate-700', icon: <Clock className="h-3 w-3" /> },
  gerada:    { cor: 'bg-blue-100 text-blue-700', icon: <FileText className="h-3 w-3" /> },
  paga:      { cor: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle2 className="h-3 w-3" /> },
  vencida:   { cor: 'bg-red-100 text-red-700', icon: <AlertCircle className="h-3 w-3" /> },
  cancelada: { cor: 'bg-slate-50 text-slate-400', icon: <Trash2 className="h-3 w-3" /> },
};

// =========================================================================
// Geração a partir de competência
// =========================================================================
const GerarTab = ({ onGerado }) => {
  const [comps, setComps] = useState([]);
  const [competenciaId, setCompetenciaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/calculos-dp/competencias`, auth())
      .then(r => setComps((r.data || []).filter((c) => ['calculada', 'fechada'].includes(c.status))))
      .catch(() => {});
  }, []);

  const gerar = async () => {
    if (!competenciaId) return toast.error('Selecione a competência');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/obrigacoes/gerar-da-competencia/${competenciaId}`, {}, auth());
      setResultado(r.data);
      toast.success(`✅ ${r.data.geradas} obrigações geradas`);
      onGerado && onGerado();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Calculator className="h-5 w-5 text-indigo-600" />Gerar Obrigações da Competência</CardTitle>
        <CardDescription>
          Agrega bases dos holerites e cria <b>GPS</b> (INSS funcionário + 20% patronal),
          <b> DARF IRRF</b> e <b>DCTFWeb</b> (totalizadora) em rascunho.
          A <b>GRRF</b> é gerada por rescisão individual na aba "GRRF".
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Competência *</Label>
          <Select value={competenciaId} onValueChange={setCompetenciaId}>
            <SelectTrigger data-testid="obrig-comp-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              {comps.length === 0 ? (
                <SelectItem value="_" disabled>Nenhuma competência calculada</SelectItem>
              ) : comps.map((c) => (
                <SelectItem key={c.id} value={c.id}>{MESES[c.mes - 1]}/{c.ano} ({c.status})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={gerar} disabled={loading || !competenciaId} className="w-full" data-testid="obrig-gerar-btn">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calculando...</> : <><Plus className="h-4 w-4 mr-2" />Gerar Obrigações</>}
        </Button>

        {resultado && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm space-y-1" data-testid="obrig-resultado">
            <div className="font-medium text-emerald-800">Competência {resultado.competencia} processada</div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <div>Folha (Proventos): <b>{fmtBRL(resultado.totais.base_folha_proventos)}</b></div>
              <div>INSS Funcionário: <b>{fmtBRL(resultado.totais.inss_funcionario)}</b></div>
              <div>INSS Patronal (20%): <b>{fmtBRL(resultado.totais.inss_patronal)}</b></div>
              <div>IRRF Retido: <b>{fmtBRL(resultado.totais.irrf)}</b></div>
              <div>FGTS: <b>{fmtBRL(resultado.totais.fgts)}</b></div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =========================================================================
// Listagem com filtros
// =========================================================================
const ListaTab = ({ refreshKey, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ tipo: '', status: '', periodo: '' });
  const [payDialog, setPayDialog] = useState(null);
  const [payDate, setPayDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
      const r = await axios.get(`${API}/api/obrigacoes`, { ...auth(), params });
      setItems(r.data || []);
    } catch { /* */ } finally { setLoading(false); }
  }, [filtros]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const { paginated, pagination, setPage } = useClientPagination(items, 20);

  const pagar = async () => {
    if (!payDialog) return;
    try {
      await axios.post(`${API}/api/obrigacoes/${payDialog.id}/pagar`,
        null, { ...auth(), params: payDate ? { data_pagamento: payDate } : {} });
      toast.success('Marcada como paga');
      setPayDialog(null); setPayDate(''); load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta obrigação?')) return;
    try { await axios.delete(`${API}/api/obrigacoes/${id}`, auth()); toast.success('Excluída'); load(); onChange && onChange(); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Filtros</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={filtros.tipo || 'all'} onValueChange={(v) => setFiltros({ ...filtros, tipo: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.keys(TIPOS).map((k) => <SelectItem key={k} value={k}>{TIPOS[k].nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filtros.status || 'all'} onValueChange={(v) => setFiltros({ ...filtros, status: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.keys(STATUS_BADGE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Período (YYYY ou YYYY-MM)</Label>
            <Input value={filtros.periodo} placeholder="2026 ou 2026-05" onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Período</th>
              <th className="text-left p-3">Nº Documento</th>
              <th className="text-right p-3">Valor</th>
              <th className="text-left p-3">Vencimento</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">Nenhuma obrigação cadastrada.</td></tr>
            ) : paginated.map((o) => {
              const sb = STATUS_BADGE[o.status] || {};
              return (
                <tr key={o.id} className="border-t hover:bg-slate-50">
                  <td className="p-3"><Badge className={`${TIPOS[o.tipo]?.cor || 'bg-slate-100'} border`}>{TIPOS[o.tipo]?.nome || o.tipo}</Badge></td>
                  <td className="p-3">{o.periodo_apuracao}</td>
                  <td className="p-3 font-mono text-[10px]">{o.numero_documento || '—'}</td>
                  <td className="p-3 text-right font-medium">{fmtBRL(o.valor_total)}</td>
                  <td className="p-3">{fmtDate(o.data_vencimento)}</td>
                  <td className="p-3"><Badge className={`inline-flex items-center gap-1 ${sb.cor}`}>{sb.icon}{o.status}</Badge></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/api/obrigacoes/${o.id}/pdf`, { ...auth(), responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                        const a = document.createElement('a'); a.href = url; a.download = `${o.tipo}_${o.numero_documento}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      } catch (e) { toast.error('Erro ao baixar PDF'); }
                    }} title="Baixar PDF da guia" data-testid={`obrig-pdf-${o.id}`}>
                      <FileText className="h-4 w-4 text-indigo-600" />
                    </Button>
                    {o.status !== 'paga' && (
                      <Button size="sm" variant="ghost" onClick={() => { setPayDialog(o); setPayDate(new Date().toISOString().slice(0, 10)); }}
                        title="Marcar como paga" data-testid={`obrig-pay-${o.id}`}>
                        <Wallet className="h-4 w-4 text-emerald-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => excluir(o.id)} title="Excluir" data-testid={`obrig-del-${o.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <GridPagination pagination={pagination} onPageChange={setPage} />
      </div>

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar como paga</DialogTitle>
            <DialogDescription>{payDialog?.numero_documento} — {fmtBRL(payDialog?.valor_total)}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Data do Pagamento</Label>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(null)}>Cancelar</Button>
            <Button onClick={pagar} data-testid="obrig-pay-confirm"><CheckCircle2 className="h-4 w-4 mr-2" />Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =========================================================================
// GRRF a partir de rescisão
// =========================================================================
const GRRFTab = ({ onGerado }) => {
  const [resc, setResc] = useState([]);
  const [rescId, setRescId] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/calculos-dp/rescisao`, auth()).then(r => setResc(r.data || [])).catch(() => {});
  }, []);

  const gerar = async () => {
    if (!rescId) return toast.error('Selecione a rescisão');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/obrigacoes/grrf/${rescId}`, {}, auth());
      setResultado(r.data);
      toast.success('GRRF gerada');
      onGerado && onGerado();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><BadgeDollarSign className="h-5 w-5 text-amber-600" />GRRF — Recolhimento Rescisório do FGTS</CardTitle>
        <CardDescription>
          Multa rescisória sobre FGTS: <b>40%</b> (sem justa causa) ou <b>20%</b> (acordo) + Contribuição Social 10% (NJC).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Rescisão *</Label>
          <Select value={rescId} onValueChange={setRescId}>
            <SelectTrigger data-testid="grrf-resc-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              {resc.length === 0 ? (
                <SelectItem value="_" disabled>Nenhuma rescisão</SelectItem>
              ) : resc.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.employee_nome} — {fmtDate(r.data_rescisao)} — {r.motivo_descricao || r.motivo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={gerar} disabled={loading || !rescId} className="w-full" data-testid="grrf-gerar-btn">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calculando...</> : <><Plus className="h-4 w-4 mr-2" />Gerar GRRF</>}
        </Button>
        {resultado && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm" data-testid="grrf-resultado">
            <div className="font-medium text-amber-800">GRRF gerada — Total {fmtBRL(resultado.valor_total)}</div>
            <div className="text-xs grid grid-cols-3 gap-2 mt-2">
              <div>Multa ({(resultado.multa_pct * 100).toFixed(0)}%): <b>{fmtBRL(resultado.multa)}</b></div>
              <div>Contrib. Social 10%: <b>{fmtBRL(resultado.contrib_social)}</b></div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =========================================================================
// PAGE
// =========================================================================
const ObrigacoesFiscaisPage = () => {
  const [sub, setSub] = useState('gerar');
  const [refreshKey, setRefreshKey] = useState(0);
  const [kpis, setKpis] = useState(null);
  const bump = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    axios.get(`${API}/api/obrigacoes/kpis`, auth()).then(r => setKpis(r.data)).catch(() => {});
  }, [refreshKey]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="obrigacoes-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Receipt className="h-6 w-6 text-indigo-600" /> Obrigações Fiscais
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Cálculo e gestão de DCTFWeb, GPS, DARF (IRRF/PIS/COFINS) e GRRF a partir das competências da folha.
        </p>
        <div className="mt-2">
          <AskMayaButton prompt="Me explique como o GridProfile calcula DCTFWeb, GPS, DARF IRRF e GRRF a partir de uma competência da folha. Quais são os vencimentos padrão de cada um e quando devo marcar como paga?" testId="ask-maya-obrigacoes" />
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="obrigacoes-kpis">
          <Card className="border-l-4 border-slate-400">
            <CardContent className="p-3"><p className="text-xs uppercase text-slate-500">Obrigações</p><p className="text-2xl font-bold">{kpis.total_count}</p></CardContent>
          </Card>
          <Card className="border-l-4 border-blue-400">
            <CardContent className="p-3"><p className="text-xs uppercase text-slate-500">Pendente</p><p className="text-xl font-bold text-blue-700">{fmtBRL(kpis.total_pendente)}</p></CardContent>
          </Card>
          <Card className="border-l-4 border-emerald-400">
            <CardContent className="p-3"><p className="text-xs uppercase text-slate-500">Pago</p><p className="text-xl font-bold text-emerald-700">{fmtBRL(kpis.total_pago)}</p></CardContent>
          </Card>
          <Card className="border-l-4 border-amber-400">
            <CardContent className="p-3"><p className="text-xs uppercase text-slate-500">Tipos Ativos</p><p className="text-2xl font-bold text-amber-700">{Object.keys(kpis.por_tipo).length}</p></CardContent>
          </Card>
        </div>
      )}

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="grid grid-cols-3 mb-4 w-full md:w-fit">
          <TabsTrigger value="gerar" data-testid="obrig-tab-gerar"><Calculator className="h-4 w-4 mr-1" />Gerar da Competência</TabsTrigger>
          <TabsTrigger value="grrf"  data-testid="obrig-tab-grrf"><BadgeDollarSign className="h-4 w-4 mr-1" />GRRF</TabsTrigger>
          <TabsTrigger value="lista" data-testid="obrig-tab-lista"><Receipt className="h-4 w-4 mr-1" />Histórico</TabsTrigger>
        </TabsList>
        <TabsContent value="gerar"><GerarTab onGerado={() => { bump(); setSub('lista'); }} /></TabsContent>
        <TabsContent value="grrf"><GRRFTab onGerado={() => { bump(); setSub('lista'); }} /></TabsContent>
        <TabsContent value="lista"><ListaTab refreshKey={refreshKey} onChange={bump} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default ObrigacoesFiscaisPage;
